import { useState } from 'react';
import type { CallerIdInfo } from '../api';
import { cleanTyped, normalizeNumber } from '../phone/number';
import type { Phone } from '../phone/phone';
import { CallingFrom } from './Dialer';

/**
 * Add someone / Transfer: one screen for both. Type the number, then pick:
 *  - Add now: they join this call as soon as they answer (nobody waits).
 *  - Transfer now: they're connected to the other person at once; you leave.
 *    No answer: the call comes back to you.
 *  - Talk first: this call waits on hold while you talk to them on a second
 *    line, then Merge (add) or Complete transfer.
 */
export function NumberPanel({
  phone,
  mode,
  who,
  conference = false,
  callerId,
  onChooseCallerId,
  onClose,
}: {
  phone: Phone;
  mode: 'add' | 'transfer';
  /** the person (or "the conference") being transferred */
  who: string;
  /** the call is a conference (transfer: everyone stays with the new person, the user leaves) */
  conference?: boolean;
  callerId: CallerIdInfo | null;
  onChooseCallerId: (choice: string) => Promise<string | null>;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState<null | 'now' | 'first'>(null);
  const [error, setError] = useState<string | null>(null);
  const number = normalizeNumber(value);
  const talkFirst = phone.canTalkFirst;
  const add = mode === 'add';

  const run = async (how: 'now' | 'first') => {
    if (!number || busy) return;
    setBusy(how);
    setError(null);
    const err =
      how === 'first' ? await phone.talkFirst(number, mode) : add ? await phone.addNow(number) : await phone.transferNow(number);
    setBusy(null);
    if (err) setError(err);
    else onClose();
  };

  return (
    <form
      className="xfer"
      onSubmit={(e) => {
        e.preventDefault();
        void run('now');
      }}
    >
      <h2 className="xfer-title">{add ? 'Add someone to the call' : conference ? 'Transfer the conference call' : `Transfer ${who}`}</h2>
      <label className="field">
        <span>Number</span>
        <input
          name="number"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          autoFocus
          placeholder="(201) 555-0123"
          value={value}
          onChange={(e) => setValue(cleanTyped(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose();
          }}
        />
      </label>
      <div className={`xfer-actions${talkFirst ? '' : ' xfer-actions-1'}`}>
        {talkFirst && (
          <button type="button" className="btn" disabled={!number || busy !== null} onClick={() => void run('first')}>
            {busy === 'first' ? 'Calling…' : 'Talk first'}
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={!number || busy !== null}>
          {busy === 'now' ? (add ? 'Adding…' : 'Transferring…') : add ? 'Add now' : 'Transfer now'}
        </button>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <ul className="xfer-choices">
        <li>
          <strong>{add ? 'Add now' : 'Transfer now'}</strong>
          {add
            ? ': they join as soon as they answer.'
            : conference
              ? ': they’re called into the call and you leave when they answer. No answer? You stay on the call.'
              : ': they’re connected right away and you leave. No answer? It comes back to you.'}
        </li>
        {talkFirst && (
          <li>
            <strong>Talk first</strong>:{' '}
            {conference && !add
              ? 'the others keep talking while you talk to them, then you complete the transfer.'
              : `this call waits on hold while you talk to them, then you ${add ? 'merge' : 'complete the transfer'}.`}
          </li>
        )}
      </ul>
      {(add || talkFirst) && <CallingFrom info={callerId} onChoose={onChooseCallerId} />}
      <button type="button" className="btn btn-quiet btn-block" onClick={onClose} disabled={busy !== null}>
        Back to the call
      </button>
    </form>
  );
}
